/**
 * The learning path's pure parts (learning-path ticket 16, ux-bug-pass-6 ticket 10): the four steps of a card's
 * track, which tab an item sits in, the one link a card opens, the "Updated" line, and what an event carries. No React
 * and no network, so each is checkable alone.
 *
 * Copy follows CLAUDE.md "UI copy": plain words and commas, no arrows, dashes or dots as separators. Spec section 8
 * forbids a price, stars, "best", "Buy", a countdown, a percentage off, Udemy's description or lesson list.
 */
import { track } from './analytics';
import type { LearningItemSet } from '../api/learning';
import type { LearningItem, LearningItemStatus, LearningOwnKind, LearningPath } from '../api/types';

// ── the step track (D5): one tap moves one step; an earlier dot steps back ──
export const LEARNING_STEPS: readonly { step: LearningItemSet; label: string }[] = [
  { step: 'todo', label: 'Not started' },
  { step: 'started', label: 'Started' },
  { step: 'halfway', label: 'Halfway' },
  { step: 'done', label: 'Done' },
];

/** Where an item is on the track, 0 to 3; -1 for `covered` and `skipped`, which have no track. */
export const stepIndex = (status: LearningItemStatus): number => LEARNING_STEPS.findIndex((s) => s.step === status);

/** The step one tap moves to, or null at Done (and for an item with no track). */
export function nextStep(status: LearningItemStatus): LearningItemSet | null {
  const i = stepIndex(status);
  return i >= 0 && i < LEARNING_STEPS.length - 1 ? LEARNING_STEPS[i + 1].step : null;
}

/** The card's tabs (D8): Picked for you holds what is not started (and `covered`), My learning what is past it. */
export type LearningTab = 'picked' | 'mine';
export const tabOf = (status: LearningItemStatus): LearningTab | null =>
  status === 'started' || status === 'halfway' || status === 'done' ? 'mine' : status === 'skipped' ? null : 'picked';
/** The items a tab lists, in the server's order. Items skipped before the steps existed are in neither (D3). */
export const itemsFor = (items: readonly LearningItem[], tab: LearningTab): LearningItem[] => items.filter((i) => tabOf(i.status) === tab);

/** "Updated today", "Updated 1 day ago", "Updated 4 days ago"; '' for a time the client cannot read. */
export function updatedLine(updatedAt: string, now: number = Date.now()): string {
  const t = Date.parse(updatedAt);
  if (Number.isNaN(t)) return '';
  const days = Math.max(0, Math.floor((now - t) / 86_400_000));
  return days === 0 ? 'Updated today' : days === 1 ? 'Updated 1 day ago' : 'Updated ' + days + ' days ago';
}

// ── the one link a card opens ──────────────────────────────────────────────
export type LearningLinkKind = 'course' | 'free' | 'search' | 'next';
export interface LearningOpenLink { kind: Exclude<LearningLinkKind, 'next'>; url: string; label: string; title: string | null; sponsored: boolean }

/**
 * The card's one link: the named course, else the free one, else Udemy's own search. An affiliate link (the course or
 * the search) is never drawn without the disclosure that goes with it, so with none to show only a free link remains.
 */
export function openLink(item: Pick<LearningItem, 'course' | 'free' | 'searchLink'>, disclosure: string): LearningOpenLink | null {
  const ok = disclosure.trim() !== '';
  if (ok && item.course) return { kind: 'course', url: item.course.url, label: 'Open course', title: item.course.title, sponsored: true };
  if (item.free) return { kind: 'free', url: item.free.url, label: 'Open course', title: item.free.title, sponsored: false };
  if (ok && item.searchLink) return { kind: 'search', url: item.searchLink, label: 'See top-rated courses on Udemy', title: null, sponsored: true };
  return null;
}

/** The path is still being made, so the tab keeps asking. Here and not in `api/hooks.ts`: the Action contract test reads that file for `.state === '...'` comparisons of a row's state. */
export const isLearningPathBuilding = (path: LearningPath | undefined) => path?.state === 'building';

// ── events (labels only: no user id, email, name, link, raw score or course text) ─────────
export const learningEvents = {
  viewed: (items: number, tab: LearningTab) => track('learning_path_viewed', { items, tab }),
  linkClicked: (skill: string, kind: LearningLinkKind) => track('learning_link_clicked', { skill, kind }),
  /** One saved step; `source` is `picked` here, `own` for a person's own item (ticket 11). */
  stepSet: (step: LearningItemSet, source: 'picked' | 'own') => track('learning_step_set', { step, source }),
  /** Own items: the kind only, never the name or the link (ticket 11, D21). */
  ownAdded: (kind: LearningOwnKind) => track('learning_own_item_added', { kind }),
  ownRemoved: (kind: LearningOwnKind) => track('learning_own_item_removed', { kind }),
};

// ── a person's own items (ticket 11) ───────────────────────────────────────
export const OWN_NAME_MAX = 120;
export const OWN_ITEMS_MAX = 50;
export const OWN_NAME_ERROR = 'Add a name.';
export const OWN_LINK_ERROR = 'That link does not look right. Start it with https://';
export const OWN_KIND_LABEL: Record<LearningOwnKind, string> = { course: 'Course', certificate: 'Certificate' };

/** The form's checks, the same as the server's: a name of 1 to 120 characters, and a link that is empty or https. */
export function checkOwnForm(name: string, link: string): { name: string | null; link: string | null } {
  const n = name.trim();
  const l = link.trim();
  const linkOk = l === '' || (/^https:\/\/[^\s]+$/.test(l) && URL.canParse(l));
  return { name: n.length >= 1 && n.length <= OWN_NAME_MAX ? null : OWN_NAME_ERROR, link: linkOk ? null : OWN_LINK_ERROR };
}
